import {useEffect, useRef, useState} from 'react'
import {IconBinaryTree2, IconId, IconPlus} from '@tabler/icons-react'
import type {NewRole} from '../api/relations'
import {fullName, spouseOf} from '../api/tree'
import type {TreeIndex} from '../api/types'

interface Props {
  ix: TreeIndex
  handle: string
  /** координаты курсора */
  at: {x: number; y: number}
  onCreate: (role: NewRole) => void
  onOpen: () => void
  onRoot: () => void
  onClose: () => void
}

interface Role {
  role: NewRole
  label: string
  /** почему пункт недоступен; пусто — доступен */
  why?: string
}

const MENU_W = 224

/**
 * Меню по правому клику на карточке древа — как в «Древе жизни».
 *
 * Роли, которые уже заняты, гасим, а не прячем: пропавший пункт читается
 * как «такого не бывает», а погашенный с подсказкой объясняет, почему.
 * Гасим именно те, где повторная привязка не добавит, а ЗАМЕНИТ имеющегося
 * родственника, — молча затереть отца страшнее, чем не дать его завести.
 */
export default function CardMenu({ix, handle, at, onCreate, onOpen, onRoot, onClose}: Props) {
  const box = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState(at)

  useEffect(() => {
    const el = box.current
    if (!el) return
    // не даём меню вылезти за экран — иначе нижние пункты недостижимы
    const r = el.getBoundingClientRect()
    setPos({
      x: Math.min(at.x, window.innerWidth - MENU_W - 8),
      y: Math.min(at.y, Math.max(8, window.innerHeight - r.height - 8)),
    })
  }, [at])

  useEffect(() => {
    const off = (e: Event) => {
      if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Escape') return
      onClose()
    }
    window.addEventListener('keydown', off)
    window.addEventListener('pointerdown', off)
    window.addEventListener('wheel', off, {passive: true})
    return () => {
      window.removeEventListener('keydown', off)
      window.removeEventListener('pointerdown', off)
      window.removeEventListener('wheel', off)
    }
  }, [onClose])

  const p = ix.people.get(handle)
  const born = ix.famOfChild.get(handle)
  const noParents = born ? '' : 'Сначала добавьте отца или мать'
  const male = p?.gender === 1
  const female = p?.gender === 0

  const roles: Role[] = [
    {role: 'son', label: 'Сын'},
    {role: 'daughter', label: 'Дочь'},
    {role: 'father', label: 'Отец', why: born?.father_handle ? 'Отец уже указан' : ''},
    {role: 'mother', label: 'Мать', why: born?.mother_handle ? 'Мать уже указана' : ''},
    {
      role: 'spouse',
      label: male ? 'Жена' : female ? 'Муж' : 'Супруг(а)',
      why: spouseOf(ix, handle) ? 'Супруг уже указан' : '',
    },
    {role: 'brother', label: 'Брат', why: noParents},
    {role: 'sister', label: 'Сестра', why: noParents},
  ]

  return (
    <div
      ref={box}
      className="cardmenu"
      style={{left: pos.x, top: pos.y, width: MENU_W}}
      onPointerDown={e => e.stopPropagation()}
      onContextMenu={e => e.preventDefault()}
    >
      <div className="who">{fullName(p)}</div>

      <div className="mlabel"><IconPlus size={12} stroke={2.2} /> Создать</div>
      <div className="mgrid">
        {roles.map(r => (
          <button
            key={r.role}
            type="button"
            disabled={!!r.why}
            title={r.why || `Создать и привязать: ${r.label.toLowerCase()}`}
            onClick={() => { onCreate(r.role); onClose() }}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="msep" />
      <button type="button" className="mitem" onClick={() => { onOpen(); onClose() }}>
        <IconId size={15} stroke={1.7} /> Открыть карточку
      </button>
      <button type="button" className="mitem" onClick={() => { onRoot(); onClose() }}>
        <IconBinaryTree2 size={15} stroke={1.7} /> Построить древо отсюда
      </button>
    </div>
  )
}
